import './Header.scss';

import {
  Avatar,
  Dropdown,
  type DropdownProps,
  Space,
  Tooltip,
  notification
} from 'antd';
import { jwtDecode } from 'jwt-decode';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import {
  ADMIN_ROUTES,
  ROUTES,
  getFullAdminRoute
} from '@/components/Shared/routes/routes';
import { DictTypes } from '@/constants';
import { DownOutlined } from '@/media/antd-icons';
import BroomIcon from '@/media/icons/broom.svg?react';
import ChevronDownIcon from '@/media/icons/chevron-down.svg?react';
import BurgerMenuIcon from '@/media/icons/menu-burger.svg?react';
import { removeCachedData } from '@/services/helpers/cacheData';
import { clearLocalStorageHelper } from '@/services/helpers/clearLocalStorage';
import { getUserFullName } from '@/services/helpers/strings';
import { getUserToken } from '@/services/user-hash';
import { useAppDispatch, useAppSelector } from '@/store';
import { setIsAuth } from '@/store/auth.slice';
import { getOptimizedDictionaries } from '@/store/dict.slice';
import { setSidebarExpanded } from '@/store/userInfo.slice';
import type { DecodedJWTToken } from '@/types/user';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { headerConfig } = useAppSelector((state) => state.app);

  const [user, setUser] = useState<{
    lastName: string;
    firstName: string;
    actions: number[];
  }>({
    firstName: '',
    lastName: '',
    actions: []
  });
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  const items: { key: string; label: React.ReactNode }[] = [
    {
      key: 'personal',
      label: (
        <Link
          to={ROUTES.personal}
          className="header__dropdown-item"
          onClick={handleCloseDropdown}
        >
          Личный кабинет
        </Link>
      )
    },
    ...(user.actions.includes(0)
      ? [
          {
            key: 'adminPage',
            label: (
              <Link
                to={getFullAdminRoute(ADMIN_ROUTES.dealers)}
                className="header__dropdown-item"
                onClick={handleCloseDropdown}
              >
                Панель администратора
              </Link>
            )
          }
        ]
      : []),
    {
      key: 'cache-clear',
      label: (
        <button
          onClick={handleClickCacheClear}
          className="header__dropdown-item header__dropdown-item--mobile"
        >
          Очистить кэш
        </button>
      )
    },
    {
      key: 'logout',
      label: (
        <button onClick={handleLogout} className="header__dropdown-item">
          Выйти
        </button>
      )
    }
  ];

  const handleOpenDropdownChange: DropdownProps['onOpenChange'] = (
    nextOpen,
    info
  ) => {
    if (info.source === 'trigger' || nextOpen) {
      setIsOpenDropdown(nextOpen);
    }
  };

  function handleCloseDropdown() {
    setIsOpenDropdown(false);
  }

  async function handleClickCacheClear() {
    removeCachedData();
    handleCloseDropdown();
    await dispatch(
      getOptimizedDictionaries({
        updatingDicts: Object.values(DictTypes),
        cacheConfig: { ignoreCache: true }
      })
    );
    notification.success({ title: 'Кэш успешно очищен' });
  }

  function handleLogout() {
    removeCachedData();
    clearLocalStorageHelper();
    dispatch(setIsAuth(false));
    handleCloseDropdown();
    navigate(ROUTES.login);
  }

  useEffect(() => {
    const accessToken = getUserToken().accessToken;
    if (accessToken) {
      const data = jwtDecode<DecodedJWTToken>(accessToken || '');
      setUser((prevState) => ({
        ...prevState,
        firstName: data?.firstName || '',
        lastName: data?.lastName || '',
        actions: data?.actions || []
      }));
    }
  }, []);

  return (
    <div className="faq-header">
      <div className="faq-header__container">
        <button
          onClick={() => dispatch(setSidebarExpanded(true))}
          className="faq-header__burger"
        >
          <BurgerMenuIcon />
        </button>

        <div
          className={`faq-header__title-wrapper ${headerConfig.hasBackArrow ? 'faq-header__title-wrapper--clickable' : ''}`}
          onClick={() => {
            if (!headerConfig.hasBackArrow) return;
            if (headerConfig.backArrowTo && headerConfig.backArrowTo !== -1) {
              navigate(headerConfig.backArrowTo);
            } else {
              navigate(-1);
            }
          }}
        >
          {headerConfig.hasBackArrow && <ChevronDownIcon />}

          <div>
            <h2 className="faq-header__title">{headerConfig.title}</h2>

            {headerConfig.hasBackArrow && (
              <p className="faq-header__secondary-text">
                {headerConfig.subtitle || 'Назад'}
              </p>
            )}
          </div>
        </div>

        <div className="faq-header__pagination">
          <div className="faq-header__language">RU</div>

          <Tooltip
            placement="bottom"
            title="Очистить кэш"
            className="header__cache"
          >
            <button onClick={handleClickCacheClear}>
              <BroomIcon />
            </button>
          </Tooltip>

          <Dropdown
            placement="bottomRight"
            arrow
            open={isOpenDropdown}
            onOpenChange={handleOpenDropdownChange}
            popupRender={() => (
              <div className="faq-header__dropdown">
                {items.map((item) => (
                  <React.Fragment key={item.key}>{item.label}</React.Fragment>
                ))}
              </div>
            )}
          >
            <Space className="faq-header__user">
              <Avatar className="faq-header__user-avatar">
                {user.firstName[0]?.toUpperCase()}
                {user.lastName[0]?.toUpperCase()}
              </Avatar>{' '}
              <p className="faq-header__user-name">
                {getUserFullName(user, { pattern: 'first_last' })}
              </p>
              <DownOutlined />
            </Space>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

export default Header;
